<template>
  <div>
    <ProductionQueueTable
      :items="queueItems"
      :countdown="countdown"
      @select="handleSelectQueueItem"
      @open-order="handleOpenOrder"
      @refresh="loadQueue"
    />

    <modal
      elem-id="pdv-producao-checkout-modal"
      :show="checkoutModalVisible"
      :title="t('Ordem de Serviço')"
      size="xlarge"
      @close="checkoutModalVisible = false"
      @dismiss="checkoutModalVisible = false"
    >
      <PDVCheckoutForm v-if="checkoutModalVisible && checkoutOrderId !== undefined && checkoutOrderId !== null" :order-id="checkoutOrderId" host="page" />
    </modal>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useSOScanner } from '../../composables/useSOScanner';
import { log, error as logError } from '../../utils/DebugLogger';
import ProductionQueueTable from './scanner/ProductionQueueTable.vue';
import PDVCheckoutForm from './PDVCheckoutForm.vue';

type Id = number | string;

interface QueueItem {
  so_order_item_id?: Id;
  [key: string]: unknown;
}

const { t } = useI18n();
const {
  queueItems,
  fetchProductionQueue,
} = useSOScanner();

const checkoutModalVisible = ref<boolean>(false);
const checkoutOrderId = ref<Id | null>(null);

const REFRESH_INTERVAL = 30;
const countdown = ref<number>(REFRESH_INTERVAL);
let refreshIntervalId: number | null = null;
let countdownIntervalId: number | null = null;

const resetCountdown = (): void => {
  countdown.value = REFRESH_INTERVAL;
};

const loadQueue = async (): Promise<void> => {
  log('PDVProducaoPage', 'loadQueue', 'Loading production queue');
  resetCountdown();
  try {
    await fetchProductionQueue();
  } catch (err) {
    logError('PDVProducaoPage', 'loadQueue', 'Error', err);
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || t('Erro ao carregar fila de produção'));
  }
};

const handleSelectQueueItem = (item: QueueItem): void => {
  if (!item?.so_order_item_id) return;
  eventBus.emit('modal.so-order-item.open', { itemId: Number(item.so_order_item_id) });
};

const handleOpenOrder = (orderId: Id): void => {
  checkoutOrderId.value = orderId;
  checkoutModalVisible.value = true;
};

onMounted(() => {
  loadQueue();
  eventBus.on('filter.updated', loadQueue);
  eventBus.on('pdv.producao.refresh', loadQueue);
  refreshIntervalId = window.setInterval(loadQueue, REFRESH_INTERVAL * 1000);
  countdownIntervalId = window.setInterval(() => {
    if (countdown.value > 0) countdown.value--;
  }, 1000);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', loadQueue);
  eventBus.off('pdv.producao.refresh', loadQueue);
  if (refreshIntervalId) window.clearInterval(refreshIntervalId);
  if (countdownIntervalId) window.clearInterval(countdownIntervalId);
});

defineExpose({ loadQueue });
</script>
